import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import './App.css';
import FileLoader from './components/FileLoader';
import TodoTreeView from './components/TodoTreeView';
import Logo from './components/Logo';
import ThemeToggle from './components/ThemeToggle';
import { TodoProvider } from './contexts/TodoContext';
import { EnhancedFileData, validateJson, readTodoFile, downloadFileWithDialog, treeToMarkdown, downloadMarkdownWithDialog } from './utils/fileHandlers';
import { TodoTree, TodayList } from './types/todo';
import { generateSampleTodoTree, createMinimalTodoTree } from './utils/todoTemplate';
import { serializeTree, loadTdlContent } from './utils/serialization';
import { EMPTY_TODAY_LIST } from './utils/today';
import { getStorageStatus } from './utils/localStorage';
import { loadAppState, debouncedSaveAppState, flushAppState, migrateLegacyLocalStorage, getStorageEstimate, isPersistenceAvailable } from './utils/persistence';
import { FILES } from './utils/constants';
import { logger } from './utils/logger';

function App() {
  const [currentFile, setCurrentFile] = useState<EnhancedFileData | null>(null);
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [fileLoadId, setFileLoadId] = useState(0);
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);
  const [storageUsage, setStorageUsage] = useState<number>(0);
  const [storageQuota, setStorageQuota] = useState<number>(0);
  const [hasSavedData, setHasSavedData] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [todayPanelCollapsed, setTodayPanelCollapsed] = useState(false);
  const currentTreeRef = useRef<TodoTree | null>(null);
  // The day plan is serialized alongside the tree, so both halves must be
  // readable synchronously whenever either one changes.
  const currentTodayRef = useRef<TodayList>(EMPTY_TODAY_LIST);
  const mountedRef = useRef(true);

  /**
   * Reconstruct EnhancedFileData from persisted JSON content. Normalizes the
   * content to the canonical v2 format so legacy/raw saved content is upgraded.
   */
  const createFileDataFromContent = (rawContent: string, filename: string): EnhancedFileData => {
    const result = loadTdlContent(rawContent);
    if (result.error || !result.tree) {
      return { filename, content: rawContent, isValid: false, error: result.error ?? 'Failed to parse saved content' };
    }
    const normalized = serializeTree(result.tree, result.rootId, result.today);
    return { filename, content: normalized, isValid: true, todoTree: result.tree, today: result.today };
  };

  /**
   * On mount: migrate any legacy localStorage state into IndexedDB, then check
   * for saved state (without auto-loading) and read the storage estimate.
   */
  useEffect(() => {
    mountedRef.current = true;

    (async () => {
      if (!isPersistenceAvailable()) {
        logger.warn('IndexedDB is not available, persistence disabled');
        return;
      }

      await migrateLegacyLocalStorage();

      const savedState = await loadAppState();
      if (mountedRef.current && savedState) {
        setHasSavedData(true);
        logger.info('Found saved state', { lastSaved: savedState.lastSaved });
      }

      const estimate = await getStorageEstimate();
      if (mountedRef.current && estimate) {
        setStorageUsage(estimate.usage);
        setStorageQuota(estimate.quota);
      }
    })();

    return () => {
      mountedRef.current = false;
      // Persist any pending debounced edit before teardown.
      void flushAppState();
    };
  }, []); // Run only on mount

  /**
   * Refresh the storage usage/quota shown in the save indicator.
   */
  const refreshStorageEstimate = useCallback(async () => {
    const estimate = await getStorageEstimate();
    if (mountedRef.current && estimate) {
      setStorageUsage(estimate.usage);
      setStorageQuota(estimate.quota);
    }
  }, []);

  /**
   * Debounced autosave to IndexedDB. Runs whenever the content, filename, or
   * help visibility changes while a file is loaded. Content is serialized
   * synchronously on every edit; only the write is debounced (and flushed on
   * page hide / teardown) to avoid a write per keystroke.
   */
  useEffect(() => {
    if (!currentFile || !content) return;

    debouncedSaveAppState(
      { content, filename: currentFile.filename, showHelp, todayPanelCollapsed },
      600,
      () => {
        if (!mountedRef.current) return;
        setLastSavedTime(new Date());
        void refreshStorageEstimate();
      }
    );
  }, [showHelp, todayPanelCollapsed, currentFile, content, refreshStorageEstimate]);

  /**
   * Flush pending saves when the page is hidden or closed so the last edit is
   * never lost (a debounced write may still be pending).
   */
  useEffect(() => {
    const handleHide = () => { void flushAppState(); };
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') void flushAppState();
    };
    window.addEventListener('pagehide', handleHide);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('pagehide', handleHide);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  /**
   * Update document title based on current filename
   */
  useEffect(() => {
    if (currentFile) {
      // Remove .tdl or .json extension if present
      const displayName = currentFile.filename.replace(/\.(tdl|json)$/i, '');
      document.title = displayName;
    } else {
      document.title = 'Better TDL';
    }
  }, [currentFile]);

  const handleFileLoad = async (file: File) => {
    setIsLoading(true);

    const fileData = await readTodoFile(file);

    // Normalize valid files (including migrated legacy files) to canonical v2
    // content in memory, so the next save writes the new format.
    if (fileData.isValid && fileData.todoTree) {
      currentTodayRef.current = fileData.today ?? EMPTY_TODAY_LIST;
      const normalized = serializeTree(fileData.todoTree, undefined, fileData.today);
      setCurrentFile({ ...fileData, content: normalized });
      setContent(normalized);
    } else {
      setCurrentFile(fileData);
      setContent(fileData.content);
    }
    setFileLoadId(prev => prev + 1); // Increment to force TodoProvider remount

    setIsLoading(false);
    // Persistence is handled by the debounced autosave effect.
  };

  const handleTreeChange = useCallback((tree: TodoTree) => {
    currentTreeRef.current = tree;
    // Serialize synchronously so the file-save button and autosave effect always
    // see current content; the autosave effect debounces the actual write.
    setContent(serializeTree(tree, undefined, currentTodayRef.current));
  }, []);

  const handleTodayChange = useCallback((today: TodayList) => {
    currentTodayRef.current = today;
    if (!currentTreeRef.current) return;
    setContent(serializeTree(currentTreeRef.current, undefined, today));
  }, []);

  const handleLoadSampleTemplate = () => {
    const sampleFlatTree = generateSampleTodoTree();
    currentTodayRef.current = EMPTY_TODAY_LIST;
    const sampleJson = serializeTree(sampleFlatTree);

    const fileData: EnhancedFileData = {
      filename: FILES.DEFAULT_SAMPLE_NAME,
      content: sampleJson,
      isValid: true,
      todoTree: sampleFlatTree,
    };

    setCurrentFile(fileData);
    setContent(sampleJson);
    setFileLoadId(prev => prev + 1); // Increment to force TodoProvider remount
  };

  const handleCreateNewList = () => {
    const newFlatTree = createMinimalTodoTree();
    currentTodayRef.current = EMPTY_TODAY_LIST;
    const newJson = serializeTree(newFlatTree);

    const fileData: EnhancedFileData = {
      filename: FILES.DEFAULT_NEW_NAME,
      content: newJson,
      isValid: true,
      todoTree: newFlatTree,
    };

    setCurrentFile(fileData);
    setContent(newJson);
    setFileLoadId(prev => prev + 1); // Increment to force TodoProvider remount
  };

  const handleResumeFromSaved = async () => {
    if (!isPersistenceAvailable()) return;

    const savedState = await loadAppState();
    if (!savedState) return;

    logger.info('Resuming from saved state', { lastSaved: savedState.lastSaved });

    const reconstructedFile = createFileDataFromContent(savedState.content, savedState.filename);

    if (reconstructedFile.isValid) {
      currentTodayRef.current = reconstructedFile.today ?? EMPTY_TODAY_LIST;
      setCurrentFile(reconstructedFile);
      setContent(reconstructedFile.content);
      setShowHelp(savedState.showHelp);
      setTodayPanelCollapsed(savedState.todayPanelCollapsed ?? false);
      setFileLoadId(prev => prev + 1); // Force TodoProvider remount
      setLastSavedTime(new Date(savedState.lastSaved));
    }
  };

  const handleSaveFile = async () => {
    if (!currentFile || !isValidJson || isDownloading) return;

    setIsDownloading(true);
    try {
      await downloadFileWithDialog(content, currentFile.filename);
    } catch (error) {
      logger.error('Download failed', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleExportMarkdown = async () => {
    if (!currentFile || !currentTreeRef.current || isDownloading) return;

    setIsDownloading(true);
    try {
      const markdown = treeToMarkdown(currentTreeRef.current);
      await downloadMarkdownWithDialog(markdown, currentFile.filename);
    } catch (error) {
      logger.error('Markdown export failed', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const isValidJson = validateJson(content).isValid;

  // Memoize the parsed tree to ensure stable references and prevent unnecessary re-renders
  const parsedTree = useMemo(() => {
    if (currentFile?.isValid && currentFile.todoTree) {
      currentTreeRef.current = currentFile.todoTree;
      return currentFile.todoTree;
    }
    return null;
  }, [currentFile]);

  return (
      <div className="App">
      <ThemeToggle />
      <main>
        {/* Toolbar - only show when file is loaded */}
        {currentFile && (
          <div className="main-toolbar">
            <div className="toolbar-actions">
              <FileLoader onFileLoad={handleFileLoad} isLoading={isLoading} />
              <button
                onClick={handleCreateNewList}
                className="toolbar-button-secondary"
              >
                ✨ New List
              </button>
              <button
                onClick={handleLoadSampleTemplate}
                className="toolbar-button-secondary"
              >
                🌳 Sample
              </button>
              {lastSavedTime && isPersistenceAvailable() && (() => {
                const storageStatus = getStorageStatus(storageUsage, storageQuota || undefined);
                return (
                <span className="toolbar-save-status" style={{
                  color: storageStatus.color,
                  backgroundColor: `${storageStatus.color}1a`,
                  border: `1px solid ${storageStatus.color}4d`
                }}>
                  💾 {lastSavedTime.toLocaleTimeString()}
                </span>
                );
              })()}
            </div>

            <div className="toolbar-right-actions">
              <button
                onClick={handleSaveFile}
                disabled={!isValidJson || !currentFile.filename || isDownloading}
                className="save-button"
                title="Save todo list to file"
              >
                {isDownloading ? 'Saving...' : '💾 Save'}
              </button>
              <button
                onClick={handleExportMarkdown}
                disabled={!currentFile.isValid || isDownloading}
                className="toolbar-button-secondary"
                title="Export as Markdown file"
              >
                📝 Export MD
              </button>
              <button
                onClick={() => setShowHelp(!showHelp)}
                className="help-button"
                title="Show/hide usage instructions"
              >
                {showHelp ? '✕ Close Help' : '? Help'}
              </button>
            </div>
          </div>
        )}

        {/* Collapsible help section */}
        {showHelp && currentFile && (
          <div className="instructions">
            <h4>How to use the interactive todo tree:</h4>
            <ul>
              <li><strong>Basic Tasks:</strong> Click checkbox to mark done/undone. Double-click names or descriptions to edit. Parents require all children completed first.</li>
              <li><strong>Adding & Organizing:</strong> Right-click tasks for quick actions (add child, set priority, archive, block/unblock, etc). Drag tasks to reorder within same parent.</li>
              <li><strong>Navigation:</strong> Zoom with Ctrl +/-/0 or bottom-left controls. Pan by dragging background. Mini-map (bottom-right) shows overview.</li>
              <li><strong>Visibility:</strong> Each task controls child visibility: "Show Active" (incomplete & non-archived), "Show All", or "Hide All" (count badge only).</li>
              <li><strong>Priority:</strong> Set via right-click menu. Visual styling: medium=italic, high/critical=bold. Colors: low=green, medium=yellow, high=orange, critical=red. Top Tasks panel ranks incomplete tasks by score.</li>
              <li><strong>Visual Cues:</strong> Status icons indicate state: ✅ completed, ⏸️ blocked, 📦 archived, ⭕ pending. Priority shown with colored icons (🔥 critical, ⚡ high, ⭐ medium, 📝 low). Highlighted tasks show animated rainbow glow effect. Progress bar shows completion percentage.</li>
              <li><strong>Today:</strong> The panel beside the tree holds the short list of what today is for. "Plan your day" walks you through carrying over unfinished items, picking tasks, and putting them in order. Removing a row only takes it off the list — the task stays in your tree. Click a row to jump to that task.</li>
              <li><strong>Auto-Save:</strong> Changes are automatically saved to your browser. File controls show last save time.</li>
              <li><strong>Undo/Redo:</strong> Ctrl+Z undoes any tree edit (including deletes); Ctrl+Shift+Z or Ctrl+Y redoes. Buttons also available in the tree header.</li>
              <li><strong>Keyboard Shortcuts:</strong> Ctrl+Z / Ctrl+Shift+Z (undo/redo), Ctrl+Enter (save description), Ctrl +/-/0 (zoom in/out/reset), ESC (cancel editing).</li>
            </ul>
          </div>
        )}

        {currentFile ? (
          <>
            {parsedTree ? (
              <TodoProvider
                key={`${currentFile.filename}-${fileLoadId}`}
                initialTree={parsedTree}
                initialToday={currentFile.today}
                onTreeChange={handleTreeChange}
                onTodayChange={handleTodayChange}
              >
                <TodoTreeView
                  filename={currentFile.filename}
                  todayPanelCollapsed={todayPanelCollapsed}
                  onToggleTodayPanel={() => setTodayPanelCollapsed(prev => !prev)}
                />
              </TodoProvider>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--interactive-danger)' }}>
                <p>Unable to load tree: Invalid todo tree format</p>
              </div>
            )}
          </>
        ) : (
          <div className="launch-page">
            <div className="launch-hero">
              <div className="hero-icon">
                <Logo width={120} height={120} />
              </div>
              <h2 className="hero-title">Welcome to Better TDL</h2>
              <p className="hero-description">
                Master complex projects with hierarchical task management.
                Organize, prioritize, and track your todos like never before.
              </p>

              <div className="feature-highlights">
                <div className="feature-item">
                  <span className="feature-icon">🌳</span>
                  <span className="feature-text">Infinite hierarchy depth</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">🎯</span>
                  <span className="feature-text">Priority-based scoring</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">📊</span>
                  <span className="feature-text">Visual tree layout</span>
                </div>
              </div>
            </div>

            <div className="launch-actions">
              <h3 className="actions-title">Get Started</h3>
              <div className="action-cards">
                {hasSavedData && (
                  <div className="action-card action-card-resume" onClick={handleResumeFromSaved}>
                    <div className="action-card-icon">🔄</div>
                    <div className="action-card-content">
                      <h4 className="action-card-title">Resume Your Work</h4>
                      <p className="action-card-description">
                        Pick up where you left off
                      </p>
                    </div>
                    <div className="action-card-arrow">→</div>
                  </div>
                )}

                <div className="action-card action-card-primary" onClick={handleLoadSampleTemplate}>
                  <div className="action-card-icon">🌳</div>
                  <div className="action-card-content">
                    <h4 className="action-card-title">Try Sample Tree</h4>
                    <p className="action-card-description">
                      Explore features with a pre-built example
                    </p>
                  </div>
                  <div className="action-card-arrow">→</div>
                </div>

                <div className="action-card" onClick={handleCreateNewList}>
                  <div className="action-card-icon">✨</div>
                  <div className="action-card-content">
                    <h4 className="action-card-title">Create New List</h4>
                    <p className="action-card-description">
                      Start fresh with an empty todo tree
                    </p>
                  </div>
                  <div className="action-card-arrow">→</div>
                </div>

                <div className="action-card action-card-file">
                  <div className="action-card-icon">📁</div>
                  <div className="action-card-content">
                    <h4 className="action-card-title">Load TDL File</h4>
                    <p className="action-card-description">
                      Import an existing todo tree
                    </p>
                  </div>
                  <FileLoader onFileLoad={handleFileLoad} isLoading={isLoading} />
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      </div>
  );
}

export default App;
